import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const services = [
    {
        title: "Marble Polishing",
        description:
            "Restore the timeless elegance of your marble surfaces. Our master craftsmen use professional-grade compounds and precision polishing techniques to bring back the mirror-like sheen of any Italian, Egyptian or local marble — leaving floors, walls and counters gleaming like new.",
        images: [
            "/Marble%20polish/banner/67011d89e8a31_Banner%20-%20-Marble-Flooring-For-Home.jpg",
            "/Marble%20polish/2.jpeg",
            "/Marble%20polish/6.jpeg",
            "/Marble%20polish/9.jpeg",
        ],
        accent: "#D4AF37",
    },
    {
        title: "Tile & Marble Installation",
        description:
            "We deliver flawless tile and marble installation for floors, walls, bathrooms and feature areas. From large-format porcelain slabs to intricate mosaic inlays, our precision-cut fitting and seamless grouting ensure every surface is a statement of quality.",
        images: [
            "/Marble%20polish/m3.jpg",
            "/Marble%20polish/3.jpeg",
            "/Marble%20polish/5.jpeg",
            "/Marble%20polish/11.jpeg",
        ],
        accent: "#C5A028",
    },
    {
        title: "Ceiling Works",
        description:
            "Elevate your interiors with bespoke ceiling designs — gypsum board, tray ceilings, coffered panels, cove lighting channels and ornate plasterwork. We transform plain ceilings into dramatic architectural centrepieces that define luxury in every room.",
        images: [
            "/Ceiling/2.jpg",
            "/Ceiling/6.webp",
            "/Ceiling/13.jpg",
            "/Ceiling/4.jfif",
        ],
        accent: "#B4901E",
    },
    {
        title: "Joinery & Carpentry",
        description:
            "Our bespoke joinery service crafts custom wardrobes, kitchen cabinetry, feature walls, doors and decorative woodwork tailored precisely to your space. Using premium hardwoods and fine finishes, we build pieces that combine function with understated luxury.",
        images: [
            "/Joinery/5.jpeg",
            "/Joinery/11.jpeg",
            "/Joinery/9.jpeg",
            "/Joinery/10.jpeg",
        ],
        accent: "#D4AF37",
    },
];

/* ─── Single service slide ─────────────────────────────────────────── */
const ServiceSlide = ({ service, index }) => {
    const isEven = index % 2 === 0;
    return (
        <div
            className="relative flex-shrink-0 flex items-center overflow-hidden"
            style={{
                width: '100vw',
                height: '100vh',
                background: isEven ? '#111111' : '#1a1a1a',
            }}
        >
            {/* giant watermark number */}
            <span
                className="absolute select-none font-serif pointer-events-none"
                style={{
                    fontSize: 'clamp(180px, 28vw, 380px)',
                    color: 'rgba(212,175,55,0.04)',
                    right: isEven ? '-0.05em' : 'auto',
                    left: isEven ? 'auto' : '-0.05em',
                    bottom: '-0.1em',
                    lineHeight: 1,
                }}
            >
                {String(index + 1).padStart(2, '0')}
            </span>

            <div
                className={`relative z-10 w-full h-full flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    } items-center`}
            >
                {/* ── Image collage ── */}
                <div className="w-full lg:w-1/2 h-[42%] lg:h-full relative overflow-hidden flex-shrink-0">
                    <img
                        src={service.images[0]}
                        alt={service.title}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30" />

                    {/* three thumbnails — hidden on very small screens to avoid overflow */}
                    <div
                        className={`absolute bottom-3 hidden xs:flex gap-1.5 px-3 sm:gap-2 sm:px-4 ${isEven ? 'right-3 sm:right-4 flex-row-reverse' : 'left-3 sm:left-4'
                            }`}
                    >
                        {service.images.slice(1).map((src, i) => (
                            <div
                                key={i}
                                className="w-14 h-14 sm:w-20 sm:h-20 lg:w-28 lg:h-28 rounded overflow-hidden border-2"
                                style={{ borderColor: service.accent }}
                            >
                                <img
                                    src={src}
                                    alt={`${service.title} ${i + 2}`}
                                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                                />
                            </div>
                        ))}
                    </div>

                    {/* index badge */}
                    <div
                        className="absolute top-4 left-4 w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-serif text-sm sm:text-lg font-bold"
                        style={{ background: service.accent }}
                    >
                        {String(index + 1).padStart(2, '0')}
                    </div>
                </div>

                {/* ── Text block ── */}
                <div className="lg:w-1/2 flex-1 lg:h-full flex flex-col justify-center px-5 sm:px-8 lg:px-16 xl:px-24 py-5 sm:py-8 lg:py-0 overflow-y-auto">
                    <p
                        className="uppercase tracking-[0.2em] sm:tracking-[0.3em] text-xs mb-2 sm:mb-3 font-semibold"
                        style={{ color: service.accent }}
                    >
                        Royal Standard · Service {String(index + 1).padStart(2, '0')}
                    </p>

                    <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl xl:text-6xl text-white mb-3 sm:mb-5 leading-tight">
                        {service.title}
                    </h2>

                    <div className="w-12 sm:w-16 h-px mb-3 sm:mb-6" style={{ background: service.accent }} />

                    <p className="text-gray-400 text-sm sm:text-base lg:text-lg leading-relaxed mb-5 sm:mb-8 max-w-md font-light line-clamp-4 sm:line-clamp-none">
                        {service.description}
                    </p>

                    <a
                        href="#contact"
                        className="inline-flex items-center gap-2 sm:gap-3 self-start px-5 sm:px-8 py-3 sm:py-4 text-white text-xs sm:text-sm uppercase tracking-widest font-semibold transition-all duration-300 group"
                        style={{
                            background: service.accent,
                            boxShadow: `0 4px 30px ${service.accent}66`,
                        }}
                    >
                        Contact Us
                        <ArrowRight
                            size={14}
                            className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

/* ─── Services section with scroll hijack ──────────────────────────── */
const Services = () => {
    const sectionRef = useRef(null);
    const [progress, setProgress] = useState(0);    // 0 → 1
    const [activeIndex, setActiveIndex] = useState(0);

    const count = services.length;

    useEffect(() => {
        const onScroll = () => {
            const el = sectionRef.current;
            if (!el) return;
            const { top, height } = el.getBoundingClientRect();
            // scrollable distance = total height − one viewport
            const scrollable = height - window.innerHeight;
            const scrolled = -top;
            const p = Math.max(0, Math.min(1, scrolled / scrollable));
            setProgress(p);
            // which panel is "current"?
            setActiveIndex(Math.min(count - 1, Math.floor(p * count)));
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [count]);

    // translate the strip: 0% at start → -(count-1)*100vw at end
    const translateX = -(progress * (count - 1) * 100); // in vw units

    return (
        <>
            {/* ── Section header (normal flow) ── */}
            <div className="bg-white py-16 text-center">
                <p className="text-yellow-600 uppercase tracking-widest text-xs font-semibold mb-2">
                    Our Capabilities
                </p>
                <h2 className="font-serif text-4xl text-gray-900">Premium Services</h2>
                <div className="w-20 h-px bg-yellow-500 mx-auto mt-5" />
                <p className="mt-4 text-gray-500 text-sm">Scroll down to explore each service</p>
            </div>

            {/*
        Tall outer wrapper = count × 100vh.
        This "holds" vertical scroll position while the sticky inner
        panel does the horizontal animation.
      */}
            <div
                id="services"
                ref={sectionRef}
                style={{ height: `${count * 100}vh`, position: 'relative' }}
            >
                {/* Sticky viewport — stays fixed while outer scrolls */}
                <div
                    style={{
                        position: 'sticky',
                        top: 0,
                        height: '100vh',
                        overflow: 'hidden',
                    }}
                >
                    {/* Horizontal strip of all slides */}
                    <div
                        style={{
                            display: 'flex',
                            width: `${count * 100}vw`,
                            height: '100%',
                            transform: `translateX(${translateX}vw)`,
                            willChange: 'transform',
                        }}
                    >
                        {services.map((service, i) => (
                            <ServiceSlide key={i} service={service} index={i} />
                        ))}
                    </div>

                    {/* Progress bar at bottom */}
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-white/10">
                        <motion.div
                            className="h-full"
                            style={{
                                backgroundColor: services[activeIndex]?.accent ?? '#D4AF37',
                                width: `${((activeIndex + 1) / count) * 100}%`,
                                transition: 'width 0.3s ease, background-color 0.5s ease',
                            }}
                        />
                    </div>

                    {/* Dot nav */}
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-20">
                        {services.map((s, i) => (
                            <div
                                key={i}
                                className="w-2 h-2 rounded-full transition-all duration-300"
                                style={{
                                    background: i === activeIndex ? s.accent : 'rgba(255,255,255,0.25)',
                                    transform: i === activeIndex ? 'scale(1.4)' : 'scale(1)',
                                }}
                            />
                        ))}
                    </div>

                    {/* Slide counter top-right */}
                    <div className="absolute top-6 right-8 text-white/50 font-serif text-sm tracking-widest">
                        <span
                            className="text-xl font-bold transition-colors"
                            style={{ color: services[activeIndex]?.accent }}
                        >
                            {String(activeIndex + 1).padStart(2, '0')}
                        </span>
                        <span className="mx-1">/</span>
                        {String(count).padStart(2, '0')}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Services;
